import React, { useState, useEffect, useRef } from 'react';
import {
  Check,
  CheckCheck,
  Clock3,
  AlertCircle,
  Pencil,
  Trash2,
  SmilePlus,
  Reply,
  Forward,
} from 'lucide-react';
import { useChatStore, reactedByMe } from '../../store/chatStore';
import { useAuthStore } from '../../store/authStore';
import { useCollectionsStore, setSelectedCollection } from '../../store/collectionsStore';
import { setActiveTab } from '../../store/uiModalStore';
import { openDocumentTab, openTaskManagerBoardTab } from '../../store/tabsStore';
import chatService from '../../services/chatService';
import { editChatMessage, deleteChatMessage, toggleChatReaction } from '../../services/chatChannelService';
import { deliverChatMessage } from '../../services/chatSend';
import EmojiReactionPicker from '../ui/EmojiReactionPicker';
import Tooltip from '../ui/Tooltip';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import UserAvatar from '../ui/UserAvatar';
import UserProfileCard from '../ui/UserProfileCard';
import { replyPreviewText } from '../../utils/chatMessagePreview';
import { useChatSearchStore } from '../../store/chatSearchStore';
import { useNotificationStore } from '../../store/notificationStore';
import { formatTime, newUid } from './chatMessageUtils';
import { ForwardChannelPicker } from './ForwardChannelPicker';
import { CODE_LANGUAGE_OPTIONS } from './ChatCodeBlock';
import { MessageContent } from './MessageContent';

/**
 * Delivery/read state for the author's own message, Telegram-style:
 * clock = sending (no server ack yet), ✓ = on the server, ✓✓ (accent) = read
 * by the other member (DM) / every receipt-sharing member (group). A failed
 * send renders as a red retry button — clicking re-sends under the same uid,
 * which the server treats idempotently, so a retry can never duplicate.
 */
export function MessageReceipt({ message: m, readCutoff, channelId }) {
  const [retrying, setRetrying] = useState(false);

  const retry = async () => {
    if (retrying) return;
    setRetrying(true);
    const store = useChatStore.getState();
    store.updateMessage(channelId, { uid: m.uid, failed: false });
    const payload = { uid: m.uid, body: m.body || '', kind: m.kind || 'text', metadata: m.metadata || {} };
    try {
      // WS when this conversation is joined; REST reaches any channel.
      const ack = await deliverChatMessage(channelId, payload);
      store.updateMessage(channelId, { uid: m.uid, id: ack.id, server_seq: ack.server_seq, failed: false });
    } catch {
      useChatStore.getState().updateMessage(channelId, { uid: m.uid, failed: true });
    } finally {
      setRetrying(false);
    }
  };

  if (m.failed) {
    return (
      <button
        type="button"
        className="chat-msg-receipt chat-msg-receipt--failed"
        title="Failed to send — click to retry"
        disabled={retrying}
        onClick={retry}
      >
        <AlertCircle size={12} />
      </button>
    );
  }
  if (!m.server_seq) {
    return (
      <span className="chat-msg-receipt chat-msg-receipt--pending" title="Sending…">
        <Clock3 size={11} />
      </span>
    );
  }
  if (readCutoff != null && readCutoff !== Infinity && m.server_seq <= readCutoff) {
    return (
      <span className="chat-msg-receipt chat-msg-receipt--read" title="Read">
        <CheckCheck size={13} />
      </span>
    );
  }
  return (
    <span className="chat-msg-receipt" title="Sent">
      <Check size={13} />
    </span>
  );
}

export const MessageRow = React.memo(function MessageRow({ message, grouped, readCutoff }) {
  const m = message;
  const authorName = m.sender?.name || 'Unknown';
  const currentUserId = useChatStore((s) => s.currentUserId);
  const channelId = useChatStore((s) => s.activeChannelId);
  const jumpHit = useChatSearchStore((s) => s.highlight?.channelId === channelId && s.highlight?.seq === m.server_seq);
  // Anchor rect for the profile card, or null when it is closed. The RECT, not
  // a boolean: the card is fixed-position and portalled, so it needs the
  // avatar's viewport box, and storing it at click time avoids re-querying a
  // node that may have scrolled or unmounted.
  const [profileRect, setProfileRect] = useState(null);
  // The email comes from the channel's own member list — Chat.Serializer's
  // `user_brief` already ships { id, name, email } on every member, so the card
  // needs no extra request and works offline like the rest of the view.
  const senderProfile = useChatStore((s) => {
    const ch = s.channels.find((c) => c.id === s.activeChannelId);
    const mem = (ch?.members || []).find(
      (mm) => String(mm.user_id) === String(message?.sender_id)
    );
    return mem?.user || null;
  });
  const currentUserName = useAuthStore((s) => s.user?.name);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [editLang, setEditLang] = useState('plaintext');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [pendingEmojis, setPendingEmojis] = useState(() => new Set());
  const reactTriggerRef = useRef(null);
  const forwardTriggerRef = useRef(null);
  const actionsRef = useRef(null);
  const editRef = useRef(null);
  // Tracks whether the code-language picker's dropdown is currently open,
  // synchronously updated (not React state) so the textarea's onBlur guard
  // below can read it mid-event without waiting on a re-render.
  const langPickerOpenRef = useRef(false);
  // Synchronous re-entry guard: saving disables the textarea, which blurs it,
  // which would call saveEdit again before the busy state has re-rendered.
  const savingRef = useRef(false);

  // Grow the edit box to fit its content — no inner scrollbar. Also undo any
  // internal scroll the caret may have caused, so the first line never slides
  // out of view under the header row.
  const autoResizeEdit = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    el.scrollTop = 0;
  };

  // On entering edit mode, focus the textarea, place the caret at the END and
  // size the box to the existing content.
  useEffect(() => {
    if (!editing) return;
    const el = editRef.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
    autoResizeEdit(el);
  }, [editing]);

  // Mine at all (including optimistic sends with no server id yet) — drives
  // the delivery/read receipt, which must show from the instant of sending.
  const isMine = m.sender_id != null && m.sender_id === currentUserId;
  // Own, persisted (has a server id), not-yet-removed message.
  const isOwn = isMine && m.id != null;
  // Only plain text / code carry an editable body.
  const canEdit = isOwn && (m.kind === 'text' || m.kind === 'code');
  // Any persisted message can be reacted to, own or not — Discord allows both.
  const canReact = m.id != null;
  // Same for reply — quoting someone else's message is the whole point.
  const canReply = m.id != null;
  // Encrypted attachments (image/file) are keyed to *this* channel, so their
  // ciphertext can't be decrypted after landing in a different one — forward
  // is only offered for content that carries over as-is.
  const canForward = m.id != null && !m.metadata?.enc;

  const startReply = () => {
    useChatStore.getState().setReplyTarget({
      id: m.id,
      sender_id: m.sender_id,
      sender_name: authorName,
      kind: m.kind,
      body: replyPreviewText(m),
    });
  };

  const forwardTo = async (targetChannelId) => {
    setForwardOpen(false);
    try {
      await chatService.sendMessage(targetChannelId, {
        uid: newUid(),
        body: m.body || '',
        kind: m.kind,
        // Marked so `has:forward` finds it (owner Q2); older forwards carry no marker.
        metadata: { ...(m.metadata || {}), forwarded: true, forwarded_from_message_id: m.id },
      });
      useNotificationStore.getState().success('Message forwarded');
    } catch {
      useNotificationStore.getState().error('Failed to forward message');
    }
  };

  const openReference = (ref) => {
    if (!ref) return;
    if (ref.type === 'document') {
      openDocumentTab({ documentId: ref.id, label: ref.label });
      setActiveTab('documents.panel');
    } else if (ref.type === 'board') {
      openTaskManagerBoardTab({ boardId: ref.id, label: ref.label });
      setActiveTab('task-manager.panel');
    } else if (ref.type === 'collection') {
      const col = useCollectionsStore.getState().collections.find((c) => c.id === ref.id);
      if (col) setSelectedCollection(col);
      setActiveTab('collections');
    }
  };

  const startEdit = () => {
    setDraft(m.body || '');
    setEditLang(m.metadata?.language || 'plaintext');
    setEditing(true);
  };

  const saveEdit = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    const body = draft.trim();
    const language = m.kind === 'code' ? editLang : undefined;
    const langChanged = m.kind === 'code' && editLang !== (m.metadata?.language || 'plaintext');
    const bodyChanged = Boolean(body) && body !== (m.body || '').trim();
    if (!body || (!bodyChanged && !langChanged)) {
      setEditing(false);
      savingRef.current = false;
      return;
    }
    setBusy(true);
    try {
      await editChatMessage(channelId, { id: m.id, body, language });
      setEditing(false);
    } catch {
      /* leave edit box open so the user can retry */
    } finally {
      setBusy(false);
      savingRef.current = false;
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await deleteChatMessage(channelId, { id: m.id });
    } catch {
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  };

  // Optimistic: flip the pill locally the instant it's clicked so reacting
  // feels instant, rather than waiting on the round trip to the server and
  // back through the broadcast echo. The server's `message_reacted` reply
  // carries the authoritative full reaction summary and overwrites this via
  // the normal updateMessage merge, so it self-corrects if we guessed wrong
  // (e.g. a concurrent toggle from another tab). On failure, roll back.
  const toggleReaction = async (emoji) => {
    if (!canReact || pendingEmojis.has(emoji)) return;
    setPendingEmojis((s) => new Set(s).add(emoji));

    // Read live store state rather than the render-time `m.reactions` prop —
    // a broadcast can land between this row's last render and the click, and
    // we need the direction to flip relative to what's actually there now.
    const liveReactions = useChatStore
      .getState()
      .messagesByChannel[channelId]?.find((x) => x.id === m.id)?.reactions;
    const iReacted = reactedByMe(liveReactions ?? m.reactions, currentUserId, emoji);
    useChatStore
      .getState()
      .toggleReactionLocal(channelId, m.id, emoji, currentUserId, currentUserName, iReacted ? 'remove' : 'add');

    try {
      await toggleChatReaction(channelId, { id: m.id, emoji });
    } catch (err) {
      // A 'timeout' only means we didn't hear back in time — the server may
      // still have applied the toggle and broadcast it (which self-corrects
      // via updateMessage). Only an explicit server error means the toggle
      // definitely never happened, so only that case rolls back our guess.
      // toggleReactionLocal reads live store state internally, so any newer
      // reaction change that arrived via broadcast while this request was in
      // flight (a different emoji, or another user's reaction) isn't
      // clobbered by reverting to the pre-click snapshot wholesale.
      if (!(err instanceof Error && err.message === 'timeout')) {
        useChatStore
          .getState()
          .toggleReactionLocal(channelId, m.id, emoji, currentUserId, currentUserName, iReacted ? 'add' : 'remove');
      }
    } finally {
      setPendingEmojis((s) => {
        const next = new Set(s);
        next.delete(emoji);
        return next;
      });
    }
  };

  const onEditKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditing(false);
    }
  };

  // Code edits keep the composer's code-mode convention: Enter inserts a
  // newline (code needs those freely), Cmd/Ctrl+Enter saves.
  const onCodeEditKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditing(false);
    }
  };

  const showReceipt = isMine && !editing;

  return (
    <div
      className={`chat-msg${grouped ? ' chat-msg--grouped' : ''}${showReceipt ? ' chat-msg--receipted' : ''}${jumpHit ? ' chat-msg--jump-hit' : ''}`}
      data-seq={m.server_seq ?? undefined}
    >
      {grouped ? (
        // Avatar-width gutter keeping grouped lines aligned; the timestamp
        // fades in on hover (Discord behaviour).
        <div className="chat-msg-gutter" aria-hidden>
          <span className="chat-msg-gutter-time">{formatTime(m.inserted_at)}</span>
        </div>
      ) : (
        /* Every avatar opens a profile card, including your own — the owner
           asked for it after using it, having originally wanted own-avatar
           inert. Consistency won: an avatar that responds on some rows and not
           others reads as a bug, and your own card is a reasonable way to check
           which account this window is signed in as.

           `type=button` + a real aria-label because this is a control, not
           decoration. */
        <button
          type="button"
          className="chat-avatar-btn"
          aria-label={`View ${authorName}'s profile`}
          onClick={(e) => setProfileRect(e.currentTarget.getBoundingClientRect())}
        >
          <UserAvatar className="chat-avatar chat-avatar--sm" userId={m.sender_id} name={authorName} />
        </button>
      )}
      {profileRect && (
        <UserProfileCard
          userId={m.sender_id}
          name={senderProfile?.name || authorName}
          email={senderProfile?.email}
          anchorRect={profileRect}
          onClose={() => setProfileRect(null)}
        />
      )}
      <div className="chat-msg-body">
        {!grouped && (
          <div className="chat-msg-meta">
            <span className="chat-msg-author" title={authorName}>{authorName}</span>
            <span className="chat-msg-time">{formatTime(m.inserted_at)}</span>
            {m.edited_at && <span className="chat-msg-edited">(edited)</span>}
          </div>
        )}

        {m.metadata?.reply_to && (
          <div className="chat-msg-reply-quote">
            <Reply size={11} className="chat-msg-reply-quote-icon" aria-hidden />
            <span className="chat-msg-reply-quote-author" title={m.metadata.reply_to.sender_name}>{m.metadata.reply_to.sender_name}</span>
            <span className="chat-msg-reply-quote-body">{m.metadata.reply_to.body}</span>
          </div>
        )}

        {editing ? (
          <div className={`chat-msg-edit${m.kind === 'code' ? ' chat-msg-edit--code' : ''}`}>
            {m.kind === 'code' && (
              <div className="chat-code-edit-wrap">
                <textarea
                  ref={editRef}
                  className="chat-composer-input chat-msg-edit-input chat-msg-edit-input--code"
                  value={draft}
                  rows={1}
                  disabled={busy}
                  spellCheck={false}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    autoResizeEdit(e.target);
                  }}
                  onKeyDown={onCodeEditKeyDown}
                  onBlur={(e) => {
                    // Clicking the picker's trigger button moves focus there
                    // (still inside this wrapper) — don't commit on that.
                    if (e.relatedTarget?.closest('.chat-code-edit-lang-picker')) return;
                    // Clicking an *option* in the dropdown also blurs the
                    // textarea, but the option list is portaled to
                    // document.body, so it's outside the wrapper above and
                    // the closest() check can't see it. Suppress the commit
                    // for the whole time the picker is open instead — the
                    // picker's onChange re-focuses the textarea once a
                    // language is actually picked, so nothing is lost.
                    if (langPickerOpenRef.current) return;
                    if (!busy) saveEdit();
                  }}
                />
                <CompactSelectPicker
                  className="chat-code-edit-lang-picker"
                  value={editLang}
                  onChange={(next) => {
                    setEditLang(next);
                    // Send focus back to the textarea so Cmd/Ctrl+Enter still
                    // saves right after picking a language.
                    editRef.current?.focus();
                  }}
                  onOpenChange={(isOpen) => {
                    langPickerOpenRef.current = isOpen;
                    // Picking a language already refocuses the textarea (see
                    // onChange above). But closing *without* picking one —
                    // Escape, or a backdrop click — just unmounts the portaled
                    // (auto-focused, searchable) dropdown with nothing to take
                    // its place, so focus falls back to <body> and Esc/Cmd+Enter/
                    // click-away-to-save all go dead until the user clicks back
                    // into the textarea by hand. Pull focus back explicitly;
                    // it's a harmless no-op if onChange already did it.
                    if (!isOpen) editRef.current?.focus();
                  }}
                  options={CODE_LANGUAGE_OPTIONS}
                  searchable
                  searchPlaceholder="Search languages…"
                  menuMinWidth={140}
                  aria-label="Code language"
                />
              </div>
            )}
            {m.kind !== 'code' && (
              <textarea
                ref={editRef}
                className="chat-composer-input chat-msg-edit-input"
                value={draft}
                rows={1}
                disabled={busy}
                onChange={(e) => {
                  setDraft(e.target.value);
                  autoResizeEdit(e.target);
                }}
                onKeyDown={onEditKeyDown}
                onBlur={() => {
                  // Clicking/tabbing away commits the edit, same as Enter.
                  if (!busy) saveEdit();
                }}
              />
            )}
            <span className="chat-msg-edit-hint">
              {m.kind === 'code'
                ? 'Cmd/Ctrl+Enter to save · Esc to cancel'
                : 'Enter to save · Shift+Enter for newline · Esc to cancel'}
            </span>
          </div>
        ) : (
          <MessageContent message={m} channelId={channelId} onOpenReference={openReference} />
        )}
        {grouped && m.edited_at && !editing && <span className="chat-msg-edited">(edited)</span>}

        {!editing && Array.isArray(m.reactions) && m.reactions.length > 0 && (
          <div className="chat-msg-reactions">
            {m.reactions.map((r) => (
              <ReactionPill
                key={r.emoji}
                emoji={r.emoji}
                users={r.users}
                mine={reactedByMe([r], currentUserId, r.emoji)}
                disabled={pendingEmojis.has(r.emoji)}
                onToggle={() => toggleReaction(r.emoji)}
              />
            ))}
          </div>
        )}
      </div>

      {showReceipt && (
        <MessageReceipt message={m} readCutoff={readCutoff} channelId={channelId} />
      )}

      {!editing && (confirmDelete || canReact || canReply || canForward || canEdit || isOwn) && (
        <div ref={actionsRef} className={`chat-msg-actions${pickerOpen || forwardOpen ? ' is-open' : ''}`}>
          {confirmDelete ? (
            <>
              <span className="chat-msg-actions-label">Delete?</span>
              <button className="chat-msg-action chat-msg-action--danger" disabled={busy} onClick={doDelete}>
                Delete
              </button>
              <button className="chat-msg-action" disabled={busy} onClick={() => setConfirmDelete(false)}>
                Cancel
              </button>
            </>
          ) : (
            <>
              {canReact && (
                <Tooltip content="Add reaction" side="top" disabled={pickerOpen}>
                  <button
                    ref={reactTriggerRef}
                    className="chat-msg-action"
                    onClick={() => setPickerOpen((o) => !o)}
                  >
                    <SmilePlus size={13} />
                  </button>
                </Tooltip>
              )}
              {canReply && (
                <Tooltip content="Reply" side="top">
                  <button className="chat-msg-action" onClick={startReply}>
                    <Reply size={13} />
                  </button>
                </Tooltip>
              )}
              {canForward && (
                <Tooltip content="Forward" side="top" disabled={forwardOpen}>
                  <button
                    ref={forwardTriggerRef}
                    className="chat-msg-action"
                    onClick={() => setForwardOpen((o) => !o)}
                  >
                    <Forward size={13} />
                  </button>
                </Tooltip>
              )}
              {canEdit && (
                <Tooltip content="Edit" side="top">
                  <button className="chat-msg-action" onClick={startEdit}>
                    <Pencil size={13} />
                  </button>
                </Tooltip>
              )}
              {isOwn && (
                <Tooltip content="Unsend" side="top">
                  <button
                    className="chat-msg-action"
                    onClick={() => setConfirmDelete(true)}
                  >
                    <Trash2 size={13} />
                  </button>
                </Tooltip>
              )}
            </>
          )}
        </div>
      )}
      {canForward && (
        <ForwardChannelPicker
          open={forwardOpen}
          anchorRef={forwardTriggerRef}
          excludeChannelId={channelId}
          onClose={() => setForwardOpen(false)}
          onSelect={forwardTo}
        />
      )}
      {canReact && pickerOpen && (
        <EmojiReactionPicker
          open={pickerOpen}
          anchorRef={actionsRef}
          onClose={() => setPickerOpen(false)}
          onSelect={(emoji) => {
            toggleReaction(emoji);
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
});

export function ReactionPill({ emoji, users, mine, disabled, onToggle }) {
  const names = users.map((u) => u.name).join(', ');
  return (
    <Tooltip content={names} side="top" delay={300}>
      <button
        type="button"
        className={`chat-msg-reaction${mine ? ' chat-msg-reaction--mine' : ''}`}
        disabled={disabled}
        onClick={onToggle}
      >
        <span className="chat-msg-reaction-emoji">{emoji}</span>
        <span className="chat-msg-reaction-count">{users.length}</span>
      </button>
    </Tooltip>
  );
}
